import React from 'react'
import PropTypes from 'prop-types'
import Icon from './icon'
import { Button } from './button'
import './field.css'

/**
 * Field Factory
 *
 * @param WField
 * @returns {FieldBox}
 * @constructor
 */
const FieldFactory = (WField) => {

  return class FieldBox extends React.Component {

    static propTypes = {
      id: PropTypes.string,
      icon: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      actions: PropTypes.array, // [ { icon: '...', action: {handler}}, ... ]
      readOnly: PropTypes.bool,
      required: PropTypes.bool
    }

    static defaultProps = {
      type: 'text',
      label: null,
      actions: [],
      readOnly: false,
      required: false,
    }

    state = {
      value: this.props.value
    }

    handleChange = (id, value) => {
      if (this.props.endorsed) {
        this.setState({ value })
      } else if (this.props.onChange) {
        this.props.onChange(id, value)
      }
    }

    handleClear = () => {
      this.handleChange(this.props.id, "")
    }

    handleEndorse = () => {
      if (this.props.onChange) this.props.onChange(this.props.id, this.state.value)
    }

    handleRecover = () => {
      const value = this.props.value ? this.props.value : ''
      this.setState({ value })
    }

    handleKeyPress = (e) => {
      switch (e.key) {
        case 'Enter' :
          e.preventDefault()
          this.handleEndorse()
          break
        case 'Escape' :
          this.handleRecover()
          break
      }
    }

    renderOKBtn = () => {
      const value = this.props.value ? this.props.value : ''
      const dirty = this.props.endorsed && this.state.value !== value
      return dirty ? [
        <Icon icon="backspace" action={this.handleRecover} small clickable secondary title="xxxx"/>,
        <Icon icon="done" action={this.handleEndorse} clickable primary/>
      ] : null
    }

    render() {
      if (this.props.endorsed) console.log("endorsed", this.props.value, this.state.value);
      const value = this.props.endorsed ? this.state.value : this.props.value
      const readOnly = this.props.readOnly ? 'readOnly' : ''
      const label    = this.props.required ? `* ${this.props.label}` : this.props.label
      const labelBox = this.props.label    ? <div className="label-box">{label}</div> : ''
      const iconBox  = this.props.icon     ? <div className="icon-box"><span className="material-icons">{this.props.icon}</span></div> : ''
      const infoBox  = this.props.info     ? <div className="info-box">{this.props.info}</div> : ''
      const clearBtn = this.props.clear    ? <Icon icon="close" small clickable action={this.handleClear} /> : ''
      return (
        <div className={`field ${this.props.className}`}>
          {labelBox}
          <div className={`field-box ${readOnly}`}>
            {iconBox}
            <WField {...this.props} value={value} onChange={this.handleChange} onKeyPress={this.handleKeyPress}/>
            {clearBtn}
            {this.renderOKBtn()}
            {this.props.children}
          </div>
          {infoBox}
        </div>
      )
    }
  }
}


/**
 * Field
 *
 * @param id
 * @param type
 * @param value
 * @param required
 * @param onChange
 * @param readOnly
 * @param placeholder
 * @returns {XML}
 * @constructor
 */
const Field = ({id, type='text', value, required, onChange, readOnly ,placeholder, onKeyPress}) => {
  const handleChange = (e) => {
    onChange(id, e.target.value)
  }
  const handleFocus = (event) => {
    event.stopPropagation()
  }
  const notEmpty = value && value.length > 0 ? 'notEmpty' : ''
  const decorator = readOnly ? 'readOnly' : ''
  return (
    <input id={id} 
      type={type} value={value} placeholder={placeholder} className={`${notEmpty} ${decorator}`}
      required={required} readOnly={readOnly}
      onChange={handleChange} onKeyDown={onKeyPress} onClick={handleFocus}/>
  )
}

const ListField = ({ values = [], renderer }) => {

  const renderItem = (value) => {
    if (renderer) {
      return renderer(value)
    } else {
      return "not found"
    }
  }
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', overflow: 'auto', height: '4rem'}}>
      {values.map(value => { return renderItem(value) })}
    </div>
  )
}

/**
 * DateField
 *
 * @param id
 * @param value
 * @param required
 * @param onChange
 * @param readOnly
 * @returns {XML}
 * @constructor
 */
const DateField = ({id, value, required, onChange, readOnly, placeholder }) => {
  const handleChange = (e) => {
    onChange(id, e.target.value)
  }
  const notEmpty = 'notEmpty'
  const decorator = readOnly ? 'readOnly' : ''
  return (
    <input id={id} type="date" value={value} placeholder={placeholder} onChange={handleChange} required={required} className={`${notEmpty} ${decorator}`} readOnly={readOnly}/>
  )
}

/**
 * Select
 *
 * @param props
 * @returns {*}
 * @constructor
 */
const Select = (props) => {
  const handleChange = (e) => {
    props.onChange(props.id, e.target.value)
  }
  const notEmpty = props.value && props.value.length > 0 ? 'notEmpty' : ''
  const decorator = props.readOnly ? 'readOnly' : ''
  const renderOption = (option, index) => {
    return (
      <option key={index} value={option.value}>{option.label}</option>
    )
  }

  const renderSelect = (props) => {
    const options = props.options || []
    return (
      <select id={props.id} required={props.required} disabled={props.disabled} value={props.value} placeholder={props.placeholder} onChange={handleChange} className={`${notEmpty} ${decorator}`}>
        <option value=""></option>
        {options.map(renderOption)}
      </select>
    )
  }

  return props.readOnly ? Field(props) : renderSelect(props)
}

/**
 * Text Area
 *
 * @param id
 * @param value
 * @param required
 * @param onChange
 * @param readOnly
 * @returns {XML}
 * @constructor
 */
const TextArea = ({ id, value, required, onChange, readOnly, placeholder }) => {
  const handleChange = (e) => {
    onChange(id, e.target.value)
  }
  const notEmpty = value && value.length > 0 ? 'notEmpty' : ''
  const decorator = readOnly ? 'readOnly' : ''
  return (
    <textarea id={id} value={value} placeholder={placeholder} onChange={handleChange} required={required} className={`${notEmpty} ${decorator}`} readOnly={readOnly}/>
  )
}

const FField = FieldFactory(Field)
const FTextArea = FieldFactory(TextArea)
const FSelect = FieldFactory(Select)
const FDate = FieldFactory(DateField)
const LField = FieldFactory(ListField)
export { LField as ListField, FField as Field, FSelect as Select, FDate as DateField, FTextArea as TextArea }



// WEBPACK FOOTER //
// ./src/lib/components/field.js